import { type ReactNode } from 'react';

/**
 * Ikonka tizimi.
 *
 * KUTUBXONA QO'SHILMADI. Lucide yoki shunga o'xshash paket 20-40 KB
 * olib keladi va bizga 20 ga yaqin ikonka kerak — bandl byudjeti
 * (200 KB gzip) uchun bu bekorga to'lov. Bu yerdagi hammasi qo'lda
 * chizilgan `path` va tree-shaking bilan faqat ishlatilgani bandlga
 * tushadi.
 *
 * TIL: 24x24, `fill: none`, `stroke: currentColor`, qalinlik 1.5,
 * uchlari va burchaklari yumaloq — `demo/avtopartshop-demo.html`
 * dagi `ic()` bilan bir xil. Kategoriya ikonkalarining shakli ham
 * demodan olingan.
 *
 * `currentColor` ATAYLAB: plitkada rang `--cat` dan, pastki menyuda
 * matn rangidan keladi. Ikonkaning ichida rang yozilsa mavzu
 * almashganda u qotib qolardi (va Stylelint uni ushlamasdi — bu TSX).
 */
export interface IconProps {
  /** Piksel o'lchami. Standart 24 — dizayn tizimidagi asosiy o'lcham. */
  size?: number;
  /**
   * Ikonka MA'NO tashiydimi.
   *
   * Standart holat — YO'Q: ikonka deyarli har doim yonidagi matnni
   * takrorlaydi va ekran o'quvchi uni ikki marta o'qib berardi.
   * Matnsiz ishlatilganda `label` beriladi.
   */
  label?: string;
}

function Svg({ size = 24, label, children }: IconProps & { children: ReactNode }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...(label === undefined
        ? { 'aria-hidden': true, focusable: false }
        : { role: 'img', 'aria-label': label })}
    >
      {children}
    </svg>
  );
}

/* ========================= kategoriyalar (6) ========================= */

export function IconEngine(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M5 9h2V6h5l2 3h3.5a2.5 2.5 0 0 1 2.5 2.5V13h1.5v3H20v1.5a2.5 2.5 0 0 1-2.5 2.5H7a2 2 0 0 1-2-2v-2H3v-4h2z" />
    </Svg>
  );
}

/**
 * Tormoz — disk va SUPPORT.
 *
 * Demoda tormoz va shina deyarli bir xil edi (ikkalasi ham ikkita
 * konsentrik doira va to'rtta chiziqcha). 23 pikselli plitkada ular
 * ajralmasdi. Support qo'shildi: siluet o'sha oilada qoladi, lekin
 * endi bir qarashda farqlanadi.
 */
export function IconBrakes(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="11" cy="12" r="8" />
      <circle cx="11" cy="12" r="3" />
      <path d="M17 8h2.5a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H17" />
    </Svg>
  );
}

export function IconOils(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 3h8v3l3 4v10a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V10l3-4z" />
      <path d="M9 13h6" />
    </Svg>
  );
}

export function IconElectric(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M13 2 4 14h6l-1 8 9-12h-6z" />
    </Svg>
  );
}

/** Shina — protektor izlari bilan (tormozdan ajralib tursin). */
export function IconTires(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 3v3.5M12 17.5V21M3 12h3.5M17.5 12H21M5.6 5.6l2.5 2.5M15.9 15.9l2.5 2.5M18.4 5.6l-2.5 2.5M8.1 15.9l-2.5 2.5" />
    </Svg>
  );
}

export function IconAccess(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </Svg>
  );
}

/* ============================== brend ============================== */

/**
 * Brend belgisi — "A" harfi va gayka.
 *
 * Boshqa ikonkalardan farqli: chiziq emas, TO'LDIRILGAN shakl va o'z
 * `viewBox` i bor (asl fayl — `docs/brand/mark.svg`). Rang baribir
 * `currentColor` dan: sarlavhada `--brand`, boshqa joyda matn rangi.
 */
export function IconLogo({ size = 24, label }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="130 145 740 740"
      fill="currentColor"
      {...(label === undefined
        ? { 'aria-hidden': true, focusable: false }
        : { role: 'img', 'aria-label': label })}
    >
      <path d="M140 860 330 170H670L860 860H690L564.8 320H435.2L310 860Z" />
      <path
        fillRule="evenodd"
        d="M599.59 462.5V577.5L500 635 400.41 577.5V462.5L500 405ZM500 440a80 80 0 1 0 0 160a80 80 0 1 0 0-160Z"
      />
    </svg>
  );
}

/* ========================== navigatsiya (5) ========================== */

export function IconHome(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5" />
    </Svg>
  );
}

/** Faol "Asosiy" tab: to'ldirilgan uy (maket). Rang `currentColor` dan. */
export function IconHomeFilled(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path
        fill="currentColor"
        d="M12 2.9 2.7 10.6a.8.8 0 0 0 .5 1.4H5v8a1.2 1.2 0 0 0 1.2 1.2h3.6v-6h4.4v6h3.6A1.2 1.2 0 0 0 19 20v-8h1.8a.8.8 0 0 0 .5-1.4z"
      />
    </Svg>
  );
}

/** Qo'ng'iroqcha (kontur) — bosh sahifa sarlavhasi. */
export function IconBell(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </Svg>
  );
}

export function IconCatalog(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </Svg>
  );
}

export function IconCart(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M2.5 3h2.2l2.3 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.2L20 7H6" />
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
    </Svg>
  );
}

export function IconOrders(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M21 8.5 12 13 3 8.5 12 4z" />
      <path d="M3 8.5v7L12 20l9-4.5v-7" />
      <path d="M12 13v7" />
    </Svg>
  );
}

export function IconProfile(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </Svg>
  );
}

/* ============================ harakatlar ============================ */

export function IconSearch(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 4.5 4.5" />
    </Svg>
  );
}

/** Garaj — mijozning mashinasi. */
export function IconCar(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3 13.5 5 8.5A2 2 0 0 1 6.9 7h10.2a2 2 0 0 1 1.9 1.5l2 5" />
      <path d="M2.5 13.5h19V18a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1h-11v1a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1z" />
      <path d="M6 16h1.5M16.5 16H18" />
    </Svg>
  );
}

/** Kuzatuv — yo'ldagi jo'natma. */
export function IconTracking(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3 7h10v10H3z" />
      <path d="M13 10h4l4 3.5V17h-8z" />
      <circle cx="7" cy="19" r="1.6" />
      <circle cx="17" cy="19" r="1.6" />
    </Svg>
  );
}

export function IconPhone(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M5 4h3.5l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 1.5V19a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </Svg>
  );
}

export function IconSend(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M21 3 3 10.5l6.5 2.5L12 20z" />
      <path d="M21 3 9.5 13" />
    </Svg>
  );
}

export function IconChevronRight(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="m9 5 7 7-7 7" />
    </Svg>
  );
}

export function IconCheck(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="m4 12.5 5 5L20 6.5" />
    </Svg>
  );
}

export function IconClose(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M5 5l14 14M19 5 5 19" />
    </Svg>
  );
}

export function IconPlus(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

export function IconMinus(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M5 12h14" />
    </Svg>
  );
}

/* ===================== admin menyusi (14) ===================== */

/*
 * Demoda yon menyu emoji bilan chizilgan (📊 ⚡ 📦 …). Emoji bu yerda
 * QAYTARILMADI: uning shakli, rangi va kengligi operatsion tizimga
 * bog'liq — Windows'dagi 🔧 va macOS'dagi 🔧 boshqa-boshqa chiqadi va
 * hech qaysi biri `currentColor` ga bo'ysunmaydi, ya'ni mavzu
 * almashganda ular qotib qoladi. Shakl demodagidek, ijro esa boshqa.
 */

export function IconBolt(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12z" />
    </Svg>
  );
}

export function IconCard(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19M6 15h3.5" />
    </Svg>
  );
}

export function IconTruck(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M2.5 6.5h11v9h-11z" />
      <path d="M13.5 10h4l3 3v2.5h-7z" />
      <circle cx="7" cy="18" r="1.8" />
      <circle cx="17" cy="18" r="1.8" />
    </Svg>
  );
}

export function IconWrench(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M15.5 3a5.5 5.5 0 0 0-5 7.7L3 18.2 5.8 21l7.5-7.5A5.5 5.5 0 1 0 15.5 3z" />
      <circle cx="16" cy="8" r="1.6" />
    </Svg>
  );
}

export function IconFolders(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M2.5 7.5V18a1.5 1.5 0 0 0 1.5 1.5h13" />
      <path d="M6 4.5h3.8l1.7 2H20a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 20 16.5H6A1.5 1.5 0 0 1 4.5 15V6A1.5 1.5 0 0 1 6 4.5z" />
    </Svg>
  );
}

export function IconUpload(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 16V3.5M7.5 8 12 3.5 16.5 8" />
      <path d="M3.5 15v3.5a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2V15" />
    </Svg>
  );
}

export function IconMap(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M9 3.5 3 6v14.5l6-2.5 6 2.5 6-2.5V3.5L15 6z" />
      <path d="M9 3.5V18M15 6v14.5" />
    </Svg>
  );
}

export function IconBuilding(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4 21V5.5a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 15 5.5V21" />
      <path d="M15 10h3.5A1.5 1.5 0 0 1 20 11.5V21M2.5 21h19" />
      <path d="M7.5 8h4M7.5 12h4M7.5 16h4" />
    </Svg>
  );
}

export function IconUsers(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 5.2a3.5 3.5 0 0 1 0 5.6M17.5 14.5a6.5 6.5 0 0 1 4 5.5" />
    </Svg>
  );
}

export function IconShuffle(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3 6h3.5l9 12H20M3 18h3.5l3-4M14 8l1.5-2H20" />
      <path d="m17.5 3.5 2.5 2.5-2.5 2.5M17.5 15.5 20 18l-2.5 2.5" />
    </Svg>
  );
}

export function IconScroll(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M6 3.5h11A1.5 1.5 0 0 1 18.5 5v14a1.5 1.5 0 0 1-1.5 1.5H6" />
      <path d="M6 3.5A1.5 1.5 0 0 0 4.5 5v2h3M6 20.5A1.5 1.5 0 0 1 4.5 19" />
      <path d="M8.5 8h7M8.5 12h7M8.5 16h4" />
    </Svg>
  );
}

export function IconGear(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4 5.3 5.3" />
    </Svg>
  );
}

export function IconPin(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 21.5s7-6.2 7-11.2a7 7 0 1 0-14 0c0 5 7 11.2 7 11.2z" />
      <circle cx="12" cy="10" r="2.6" />
    </Svg>
  );
}

export function IconChart(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3.5 20.5h17" />
      <path d="M6.5 17V11M11 17V4.5M15.5 17v-4M20 17V8" />
    </Svg>
  );
}

/* ===================== qo'shimcha kategoriyalar (10) =====================
 * Har kategoriya o'z ikonkasiga ega: bir xil ikonka turli kategoriyada mijozni chalg'itadi.
 * Hammasi bitta tilda: viewBox 24, chiziq 1.5, yumaloq uchlar. */

export function IconSuspension(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 2.5v4.5" />
      <rect x="10" y="7" width="4" height="5" rx="1" />
      <path d="M7.5 12.5l9 1.6-9 1.6 9 1.6-9 1.6" />
      <circle cx="12" cy="21" r="1.2" />
    </Svg>
  );
}

export function IconLighting(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3 8.8c0-1 .8-1.8 1.8-1.8H9c3.3 0 6 2.4 6 5s-2.7 5-6 5H4.8c-1 0-1.8-.8-1.8-1.8z" />
      <path d="M18 8.5h3M18.5 12H22M18 15.5h3" />
    </Svg>
  );
}

export function IconFilters(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M7 7V5h10v2" />
      <rect x="6" y="7" width="12" height="12" rx="2" />
      <path d="M9.5 10.5v5M12 10.5v5M14.5 10.5v5" />
    </Svg>
  );
}

export function IconCooling(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <rect x="4.5" y="6" width="15" height="12" rx="2" />
      <path d="M8.5 6v12M12 6v12M15.5 6v12M2.5 9.5v5M21.5 9.5v5" />
    </Svg>
  );
}

export function IconClimate(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9" />
      <path d="M9.6 4.8L12 7l2.4-2.2M9.6 19.2L12 17l2.4 2.2" />
    </Svg>
  );
}

export function IconEv(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M9 3v4M15 3v4" />
      <path d="M7 7h10v4a5 5 0 0 1-10 0z" />
      <path d="M12 16v5" />
    </Svg>
  );
}

export function IconElectronics(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <rect x="7" y="7" width="10" height="10" rx="2" />
      <rect x="10" y="10" width="4" height="4" rx=".6" />
      <path d="M10 4v3M14 4v3M10 17v3M14 17v3M4 10h3M4 14h3M17 10h3M17 14h3" />
    </Svg>
  );
}

export function IconTransmission(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M6 6v12M12 6v12M18 6v12M6 12h12" />
      <circle cx="6" cy="5" r="1.6" />
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="18" cy="5" r="1.6" />
      <circle cx="6" cy="19" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
      <circle cx="18" cy="19" r="1.6" />
    </Svg>
  );
}

export function IconBody(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3 16v-3.2c0-.6.2-1.1.6-1.5L6 8.8c.4-.5 1-.8 1.7-.8h6c.6 0 1.2.3 1.6.7l3.2 3.1c.3.3.5.7.5 1.2V16" />
      <path d="M3 16h2.2M9.8 16h4.4M18.8 16H21" />
      <circle cx="7.5" cy="16.5" r="2" />
      <circle cx="16.5" cy="16.5" r="2" />
    </Svg>
  );
}

export function IconFuel(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M5 20V5.5A1.5 1.5 0 0 1 6.5 4h6A1.5 1.5 0 0 1 14 5.5V20" />
      <path d="M4 20h11M7.5 7.5h4" />
      <path d="M14 9h2a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 3 0v-6l-2.5-2.5" />
    </Svg>
  );
}

/* ===================== kategoriya kaliti bo'yicha ===================== */

const BY_KEY: Record<string, (props: IconProps) => ReactNode> = {
  engine: IconEngine,
  brakes: IconBrakes,
  oils: IconOils,
  electric: IconElectric,
  tires: IconTires,
  access: IconAccess,
  suspension: IconSuspension,
  lighting: IconLighting,
  filters: IconFilters,
  cooling: IconCooling,
  climate: IconClimate,
  ev: IconEv,
  electronics: IconElectronics,
  transmission: IconTransmission,
  body: IconBody,
  fuel: IconFuel,
};

/**
 * Kategoriya ikonkasi — `iconKey` bo'yicha.
 *
 * Noma'lum kalit `access` ga tushadi: yangi kategoriya qo'shilganda
 * ekranda BO'SH JOY qolmasligi kerak. Bu demodagi
 * `ICON[k] || ICON.access` bilan bir xil xatti-harakat.
 */
export function CategoryIcon({
  iconKey,
  ...props
}: IconProps & { iconKey: string | null }): ReactNode {
  const Component = BY_KEY[iconKey ?? ''] ?? IconAccess;
  return <Component {...props} />;
}

/**
 * Kategoriya tasviri: admin rasm yuklagan bo'lsa — o'sha rasm (dekorativ,
 * nom yonida turadi), bo'lmasa kategoriya ikonkasi. `imageUrl` allaqachon
 * ilova ko'ra oladigan to'liq manzil bo'lishi kerak.
 */
export function CategoryVisual({
  iconKey,
  imageUrl,
  size,
}: {
  iconKey: string | null;
  imageUrl: string | null | undefined;
  size: number;
}): ReactNode {
  // Bo'sh/yo'q qiymat — rasm emas: singan `<img>` o'rniga ikonka.
  if (imageUrl === null || imageUrl === undefined || imageUrl === '')
    return <CategoryIcon iconKey={iconKey} size={size} />;
  return (
    <img
      className="aps-category-image"
      src={imageUrl}
      alt=""
      width={56}
      height={56}
      loading="lazy"
      decoding="async"
    />
  );
}

/** Uch nuqta — "Boshqalar" plitkasi (to'liq katalogga olib boradi). */
export function IconMore(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="5.5" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.6" fill="currentColor" />
    </Svg>
  );
}
